import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { ArrowRight, CheckCircle } from "lucide-react";
import { Helmet } from "react-helmet-async";

const IMAGE = "https://customer-assets.emergentagent.com/job_cayenne-processor/artifacts/9mvvrwh4_IMG-20260415-WA0050.jpg";

export default function FrozenPulpPage() {
  return (
    <main data-testid="frozen-pulp-page" className="pt-20">
      <Helmet>
        <title>Frozen Pineapple Pulp | Novapine | Bulk Supplier South Africa</title>
        <meta name="description" content="Buy frozen pineapple pulp in bulk from Novapine, Hluhluwe. Processed from Cayenne pineapples grown on Mkonge Farm. Ideal for juice manufacturers, beverage producers, and food processing. Reliable supply across South Africa." />
        <meta name="keywords" content="frozen pineapple pulp, pineapple pulp supplier South Africa, bulk pineapple pulp, pineapple juice ingredient, frozen fruit pulp, Cayenne pineapple pulp, Novapine, Mkonge Farm, Hluhluwe" />
        <link rel="canonical" href="https://www.novapine.co.za/products/frozen-pulp" />
        <meta property="og:title" content="Frozen Pineapple Pulp | Novapine | Bulk Supplier South Africa" />
        <meta property="og:description" content="Buy frozen pineapple pulp in bulk. Processed from Cayenne pineapples on Mkonge Farm, Hluhluwe. Reliable supply for juice and beverage manufacturers." />
        <meta property="og:image" content={IMAGE} />
      </Helmet>

      <section className="py-20 bg-novapine-brown">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <p className="font-heading text-xs uppercase tracking-[0.2em] font-semibold text-novapine-gold mb-4">
            Product
          </p>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tighter text-white mb-4">
            Frozen Pineapple Pulp
          </h1>
          <p className="font-body text-base text-white/70 max-w-lg mx-auto">
            High-quality frozen pulp processed from Cayenne pineapples grown on Mkonge Farm, Hluhluwe.
          </p>
        </div>
      </section>

      <section className="py-24 bg-[#F9F6F0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img src={IMAGE} alt="Frozen pineapple pulp processing at Novapine" className="rounded-xl w-full h-[450px] object-cover" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-novapine-brown mb-6">
                Bulk Frozen Pineapple Pulp for Commercial Use
              </h2>
              <p className="font-body text-base text-novapine-brown/70 leading-relaxed mb-6">
                Our frozen pineapple pulp is extracted from locally sourced Cayenne pineapples and frozen to preserve flavour, colour, and nutritional value. Produced at our processing facility in Hluhluwe, KwaZulu-Natal, and available in bulk quantities for commercial and industrial buyers.
              </p>
              <p className="font-body text-base text-novapine-brown/70 leading-relaxed mb-8">
                With a strong focus on consistency and quality, Novapine supplies frozen pineapple pulp to juice manufacturers, beverage companies, and food processors across South Africa — including major brands like Sir Fruit and Mr Juice.
              </p>

              <h3 className="font-heading font-semibold text-sm uppercase tracking-[0.1em] text-novapine-gold mb-4">
                Applications
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-8">
                {["Juice manufacturing", "Smoothie production", "Beverage concentrates", "Flavoured water and health drinks", "Food processing ingredients", "Retail products"].map((app) => (
                  <div key={app} className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-novapine-gold shrink-0" />
                    <span className="font-body text-sm text-novapine-brown/70">{app}</span>
                  </div>
                ))}
              </div>

              <Link to="/contact">
                <Button className="btn-gold rounded-md px-8 py-5 font-heading text-sm tracking-wide font-semibold">
                  Enquire About Frozen Pulp <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-novapine-brown mb-6">
            Why Choose Novapine Frozen Pineapple Pulp?
          </h2>
          <div className="space-y-4">
            <p className="font-body text-base text-novapine-brown/70 leading-relaxed">
              <strong className="text-novapine-brown">Farm-to-product traceability.</strong> All our Cayenne pineapples are grown on Mkonge Farm in Hluhluwe, giving us full control over quality from cultivation to processing.
            </p>
            <p className="font-body text-base text-novapine-brown/70 leading-relaxed">
              <strong className="text-novapine-brown">Consistent supply.</strong> With an established farming and processing operation since 2007, we deliver reliable, year-round supply to commercial partners across South Africa.
            </p>
            <p className="font-body text-base text-novapine-brown/70 leading-relaxed">
              <strong className="text-novapine-brown">Trusted by leading brands.</strong> Novapine currently supplies frozen pineapple pulp to Sir Fruit, NuLeaf, Mr Juice, Euroberry, and all major fresh produce markets in South Africa.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-novapine-brown">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-white mb-6">
            Ready to Order Frozen Pineapple Pulp?
          </h2>
          <p className="font-body text-base text-white/70 mb-10">
            Contact our team to discuss volumes, pricing, and delivery.
          </p>
          <Link to="/contact">
            <Button className="btn-gold rounded-md px-10 py-6 font-heading text-sm tracking-wide font-semibold">
              Get In Touch <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
}
